import { Card, Col, Row, Segmented, SegmentedProps, Space, Typography } from 'antd';
import { FC, memo, useEffect, useState } from 'react';
import { BorderStyle, BorderWidth } from 'react-bootstrap-icons';
import { useIsFetching } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppRoutes } from '@/common/interfaces';
import { CommonContainerTitle } from '@/common/layouts';

import {
    PageNodesOverview,
    PageNodesQueryKeys,
    PagesList,
    PagesMassActionsDropdown,
    PagesQueryKeys,
    PagesViewMode,
} from '@/modules/Pages';

interface Props {
  siteId: string;
}

export const PagesOverviewContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pagesListViewMode, setPagesListViewMode] = useState<PagesViewMode>(() => {
    if (location.pathname.includes(`/${PagesViewMode.Structure}`)) return PagesViewMode.Structure;
    else return PagesViewMode.Pages;
  });

  const pagesViewModeOptions: SegmentedProps['options'] = [
    {
      label: 'За сторінками',
      icon: <BorderWidth />,
      value: PagesViewMode.Pages,
    },
    {
      label: 'За структурою',
      icon: <BorderStyle />,
      value: PagesViewMode.Structure,
    },
  ];

  useEffect(() => {
    if (location.pathname.includes(`/${PagesViewMode.Structure}`)) {
      setPagesListViewMode(PagesViewMode.Structure);
    } else {
      setPagesListViewMode(PagesViewMode.Pages);
    }
  }, [location.pathname]);

  const isPagesViewModeFecthing = !!useIsFetching({ queryKey: [PagesQueryKeys.GetAll] });
  const isStructureViewModeFecthing = !!useIsFetching({ queryKey: [PageNodesQueryKeys.GetOneById] });

  const handleChangeViewMode = (mode: SegmentedProps['value']): void => {
    if (mode === PagesViewMode.Pages) {
      navigate(AppRoutes.Pages.replace(':siteId', siteId));
    }
    if (mode === PagesViewMode.Structure) {
      navigate(AppRoutes.PageNodes.replace(':siteId', siteId));
    }
  };

  const handleBack = (): void => {
    navigate(AppRoutes.SitesById.replace(':siteId', siteId));
  };

  return (
    <>
      <CommonContainerTitle
        onBack={handleBack}
        extra={
          <>
            <PagesMassActionsDropdown siteId={siteId} disabled={isPagesViewModeFecthing || isStructureViewModeFecthing} />

            <Card styles={{ body: { paddingBlock: 2, paddingInline: 8 } }}>
              <Space size="small">
                <Typography.Text type="secondary">Режим перегляду:</Typography.Text>
                <Segmented
                  disabled={isPagesViewModeFecthing || isStructureViewModeFecthing}
                  options={pagesViewModeOptions}
                  value={pagesListViewMode}
                  onChange={handleChangeViewMode}
                />
              </Space>
            </Card>
          </>
        }
      >
        <>
          {(pagesListViewMode === PagesViewMode.Pages && 'Сторінки') ||
            (pagesListViewMode === PagesViewMode.Structure && 'Структура')}
        </>
      </CommonContainerTitle>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          {pagesListViewMode === PagesViewMode.Pages && <PagesList siteId={siteId} />}
          {pagesListViewMode === PagesViewMode.Structure && <PageNodesOverview siteId={siteId} />}
        </Col>
      </Row>
    </>
  );
});
